import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faPlus,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { faExclamationTriangle } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { MissionControlLoadingBar } from "@/components/mission-control/MissionControlLoading";
import { useVirtualScrollElement } from "@/components/mission-control/useVirtualScrollElement";
import { useDeepLinkFocus } from "@/components/ui/deep-link-focus/useDeepLinkFocus";
import {
  useOpenInNewWindowMenu,
  useOsActionsOptional,
  useSetWindowTitle,
  useWindowById,
  useWindowId,
} from "@/components/ui/os-layout";
import {
  WORKBENCH_MUTED_TEXT_CLASS,
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SUBTLE_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import { useAgenticSubscription } from "@/hooks/useAgenticSubscription";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type {
  MissionSummary,
  MissionTemplateSummary,
} from "@/services/api/agentic-v2";
import {
  buildMissionsPageKeyInput,
  createMissionsPageWsSubscription,
  useMissionsPage,
  useMissionsPageQueryClient,
} from "@/services/api/agentic-v2/queries/useMissionsPage";
import { missionControlKeys } from "@/services/api/agentic-v2/queries/keys";
import { MissionsFilterBar } from "./MissionsFilterBar";
import { MissionCreateDialog } from "./MissionCreateDialog";
import { MissionsVirtualList } from "./MissionsVirtualList";
import { DEFAULT_MISSIONS_FILTERS, type MissionsFilters } from "./missionsFilters";
import { useInfiniteScrollLoadMore } from "@/components/mission-control/useInfiniteScrollLoadMore";
import { useAgentsList } from "./useAgentsList";
import { buildMissionDetailWindowUrl } from "@/navigation/apps/missionDetailLocation";

/**
 * Top-level Missions list view (mockup screen).
 *
 * Wires together:
 * - search input
 * - inline Type / Assigned / Status filters
 * - rows of MissionRow
 * - "+ New Mission" dialog
 */
type MissionsListProps = {
  focusKey?: string | null;
};

export function MissionsList({ focusKey: focusKeyProp = null }: MissionsListProps = {}) {
  const [searchParams] = useSearchParams();
  const windowId = useWindowId();
  const osWindow = useWindowById(windowId ?? "");
  const focusKey = focusKeyProp ?? osWindow?.params?.focus ?? searchParams.get("focus");
  useDeepLinkFocus(focusKey);

  const setWindowTitle = useSetWindowTitle();
  const titleSetRef = useRef(false);
  useEffect(() => {
    if (titleSetRef.current) return;
    setWindowTitle("Missions");
    titleSetRef.current = true;
  }, [setWindowTitle]);
  const navigate = useNavigate();
  const osActions = useOsActionsOptional();
  const newWindowMenu = useOpenInNewWindowMenu();
  const isWindowLive = useWindowLive();
  const queryClient = useMissionsPageQueryClient();
  const {
    ref: scrollRef,
    refObject: scrollRefObject,
    element: scrollElement,
    scrollReady,
  } = useVirtualScrollElement<HTMLDivElement>();

  const [filters, setFilters] =
    useState<MissionsFilters>(DEFAULT_MISSIONS_FILTERS);
  const [templates, setTemplates] = useState<MissionTemplateSummary[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const missionsKeyInput = useMemo(
    () => buildMissionsPageKeyInput(filters),
    [filters],
  );

  const missionsQuery = useMissionsPage({ filters, enabled: true });
  const missions = missionsQuery.missions;
  const loading = missionsQuery.isLoading && missions.length === 0;
  const refreshing =
    missionsQuery.isFetching && !loading && !missionsQuery.isFetchingNextPage;

  useInfiniteScrollLoadMore({
    scrollElement,
    hasMore: missionsQuery.hasNextPage ?? false,
    loading: missionsQuery.isFetchingNextPage,
    onLoadMore: () => {
      void missionsQuery.fetchNextPage();
    },
    itemsCount: missions.length,
  });
  const error = missionsQuery.error;
  const agents = useAgentsList();

  useLayoutEffect(() => {
    const el = scrollRefObject.current;
    if (el) el.scrollTop = 0;
  }, [missionsKeyInput.filtersKey, scrollRefObject]);

  const invalidateMissionsPage = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: missionControlKeys.missionsPage(missionsKeyInput),
    });
  }, [missionsKeyInput, queryClient]);

  const wsSubscription = useMemo(
    () => createMissionsPageWsSubscription(queryClient, missionsKeyInput),
    [missionsKeyInput, queryClient],
  );
  useAgenticSubscription(wsSubscription, { enabled: isWindowLive });

  useEffect(() => {
    let mounted = true;
    AgenticV2API.listMissionTemplates()
      .then((rows) => {
        if (mounted) setTemplates(rows);
      })
      .catch(() => {
        // Templates are non-critical for the list view; ignore failure.
      });
    return () => {
      mounted = false;
    };
  }, []);

  const navigateToMission = (mission: MissionSummary) => {
    if (osActions) {
      osActions.openWindow({
        appId: "mission",
        title: missionWindowTitle(mission),
        params: { missionId: mission._id },
      });
      return;
    }
    navigate(buildMissionDetailWindowUrl(mission._id));
  };

  const handleCreated = (missionId: string) => {
    setCreateOpen(false);
    invalidateMissionsPage();
    if (osActions) {
      osActions.openWindow({
        appId: "mission",
        title: "Mission",
        params: { missionId },
      });
    } else {
      navigate(buildMissionDetailWindowUrl(missionId));
    }
  };

  return (
    <div
      className={clsx("flex flex-col h-full w-full p-4", WORKBENCH_ROOT_TEXT_CLASS)}
      data-testid="missions-list"
    >
     <div className={clsx("flex-1 min-h-0 flex flex-col rounded-2xl px-6 py-5", WORKBENCH_SURFACE_CLASS)}>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <FormInput
            type="search"
            placeholder="Search missions..."
            value={filters.q}
            icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
            iconPosition="start"
            data-testid="missions-search-input"
            className="rounded-md border border-slate-200 bg-white py-2.5 text-sm text-slate-900 placeholder:text-slate-500 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-200"
            onChange={(e) =>
              setFilters({ ...filters, q: e.target.value })
            }
          />
        </div>
        <Button color="indigo" onClick={() => setCreateOpen(true)}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          New Mission
        </Button>
      </div>

      <MissionsFilterBar
        filters={filters}
        templates={templates}
        onChange={setFilters}
      />

      <div className="relative mt-6 flex-1 min-h-0 flex flex-col">
        <MissionControlLoadingBar active={refreshing} />
        {loading && missions.length === 0 ? (
          <div className="space-y-2">
            <LoadingPlaceholder height="56px" />
            <LoadingPlaceholder height="56px" />
            <LoadingPlaceholder height="56px" />
          </div>
        ) : error && missions.length === 0 ? (
          <ErrorState message={error.message} onRetry={invalidateMissionsPage} />
        ) : missions.length === 0 ? (
          <EmptyState filters={filters} />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className={clsx("text-xs mb-2 shrink-0", WORKBENCH_MUTED_TEXT_CLASS)}>
              {missionsCountLabel(missions.length, filters)}
            </div>
            <div
              ref={scrollRef}
              className={clsx(
                "flex min-h-0 flex-1 flex-col overflow-y-auto",
                refreshing && "pointer-events-none opacity-60 transition-opacity duration-200",
              )}
            >
              <MissionsVirtualList
                key={missionsKeyInput.filtersKey}
                missions={missions}
                agents={agents}
                refreshing={refreshing}
                onMissionClick={navigateToMission}
                onMissionContextMenu={(event, mission) =>
                  newWindowMenu.onContextMenu(
                    event,
                    buildMissionDetailWindowUrl(mission._id),
                    missionWindowTitle(mission),
                  )
                }
                scrollElement={scrollElement}
                scrollReady={scrollReady}
                listKey={missionsKeyInput.filtersKey}
              />
              {newWindowMenu.menu}
            </div>
          </div>
        )}
      </div>

     </div>

      <MissionCreateDialog
        open={createOpen}
        templates={templates}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}

function EmptyState({ filters }: { filters: MissionsFilters }) {
  const hasActiveFilters =
    filters.templateId !== "all" ||
    filters.statusFilter !== "all" ||
    filters.assigned !== "everyone" ||
    filters.q.trim() !== "";

  return (
    <div className={clsx("flex-1 flex flex-col items-center justify-center py-12", WORKBENCH_MUTED_TEXT_CLASS)}>
      <p className="text-sm">
        {hasActiveFilters
          ? "No missions match the current filters."
          : "No missions yet — create one to get started."}
      </p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className={clsx("flex-1 flex flex-col items-center justify-center py-12 gap-3", WORKBENCH_SUBTLE_TEXT_CLASS)}>
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        className="text-amber-500 text-2xl"
      />
      <p className="text-sm">Failed to load missions: {message}</p>
      <Button onClick={onRetry}>Retry</Button>
    </div>
  );
}

function missionsCountLabel(count: number, filters: MissionsFilters): string {
  if (filters.statusFilter === "needs_attention") {
    return `${count} mission${count === 1 ? "" : "s"} need${count === 1 ? "s" : ""} your attention`;
  }
  return `${count} mission${count === 1 ? "" : "s"}`;
}

function missionWindowTitle(mission: MissionSummary): string {
  const entity = mission.entities[0]?.display_name || "Mission";
  const tmpl = mission.template_name || "";
  return tmpl ? `${entity} — ${tmpl}` : entity;
}
